/**
 * Modal -- the single accessible dialog primitive for the CRM (#607, #600).
 *
 * Replaces the hand-rolled overlay markup that every modal used to re-implement.
 * The quality/security audit found 0/12 modals used role="dialog"/aria-modal,
 * none trapped focus, and only 2 handled Escape. This primitive fixes all of
 * that in one place so adopting modals get a11y for free:
 *
 *   - role="dialog" + aria-modal="true", labelled by the title (aria-labelledby).
 *   - Focus trap: focus moves into the dialog on open, Tab / Shift-Tab cycle
 *     within it, and focus returns to the element that had it before opening
 *     (typically the trigger button) on close.
 *   - Escape closes (parity with the X / Cancel buttons; opt-out via
 *     closeOnEscape={false}).
 *   - Backdrop click closes (opt-out via closeOnBackdrop={false}).
 *   - closeDisabled blocks every dismiss path at once (Escape, backdrop and
 *     the X button, which renders disabled) -- e.g. while a request that
 *     must not be abandoned mid-flight is running.
 *   - Background scroll locked via the shared useBodyScrollLock hook.
 *   - CRM bottom-sheet-on-mobile pattern: overlay items-end sm:items-center,
 *     panel rounded-t-xl sm:rounded-lg max-h-[95vh]. Footer slots should use
 *     flex-col-reverse sm:flex-row so the primary action sits on top on mobile.
 *   - Rendered through a React portal to document.body (#1815). A position:
 *     fixed overlay is positioned relative to its nearest ancestor that
 *     establishes a containing block (transform / filter / BACKDROP-FILTER /
 *     perspective / contain), NOT the viewport. The app-shell headers carry a
 *     frosted-glass `backdrop-blur` (#1718/#1739), so a modal opened from a
 *     trigger mounted INSIDE the header (e.g. the global FeedbackButton) had
 *     its `fixed inset-0` overlay trapped inside the ~68px-tall header box:
 *     the backdrop covered only the header strip and the centered panel was
 *     pushed up and cropped off the top of the screen. Portalling to
 *     document.body escapes every such containing block so the overlay always
 *     fills the true viewport and the panel centres correctly.
 *
 * Two usage shapes are supported, matching the existing modals:
 *   1. Conditionally-rendered:  {show && <Modal onClose={...}>...}</Modal>}
 *      (open defaults to true, so simply mounting the element opens it).
 *   2. open-prop controlled:    <Modal open={open} onClose={...}>...}</Modal>
 *      (renders null while closed).
 *
 * Styling: Tailwind utility classes + brand CSS-variable tokens only, with
 * dark: variants. No custom CSS.
 */
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";

// Discreet in-modal brightmark feedback trigger (#628). Lazy so the base Modal chunk stays lean —
// only modals that actually render it pull in FeedbackModal + the brightmark lazy loader. Ringfenced
// to the components/feedback boundary; the capture library itself loads later on first click.
const ModalAnnotateTrigger = lazy(() =>
  import("../feedback/annotate/ModalAnnotateTrigger").then((m) => ({
    default: m.ModalAnnotateTrigger,
  })),
);

export type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-xl",
  "2xl": "sm:max-w-2xl",
  "3xl": "sm:max-w-3xl",
};

export interface ModalProps {
  /**
   * Whether the modal is open. Defaults to true so conditionally-rendered
   * modals ({show && <Modal/>}) work by simply mounting. open-prop controlled
   * modals pass their boolean here and the modal renders null while closed.
   */
  open?: boolean;
  /** Called when the user requests close (Escape, backdrop click, X button). */
  onClose: () => void;
  /** Heading text -- becomes the dialog's accessible name (aria-labelledby). */
  title: ReactNode;
  /** Optional icon shown before the title (e.g. a Lucide icon element). */
  titleIcon?: ReactNode;
  /** Dialog body. Adopting modals pass their <form> / content here. */
  children: ReactNode;
  /**
   * Optional sticky footer (actions). Use a flex-col-reverse sm:flex-row layout
   * with the primary action first so it lands on top on mobile.
   */
  footer?: ReactNode;
  /** Max width on desktop. Default "lg". */
  size?: ModalSize;
  /** Close when the backdrop is clicked. Default true. */
  closeOnBackdrop?: boolean;
  /** Close when Escape is pressed. Default true. */
  closeOnEscape?: boolean;
  /**
   * Block every dismiss path (Escape, backdrop, X button -- the X renders
   * disabled). Default false. Footer buttons are the caller's to disable.
   */
  closeDisabled?: boolean;
  /**
   * Render no close (X) button at all. Default false. For a dialog the user
   * must answer before anything else loads (e.g. the client portal's privacy
   * notice, D9-SEC-011); pair with closeOnEscape/closeOnBackdrop={false}.
   */
  hideClose?: boolean;
  /** Accessible label for the close (X) button. Default "Close". */
  closeLabel?: string;
  /** Extra classes for the dialog panel. */
  className?: string;
  /**
   * When the body should scroll independently of a fixed header/footer (tall
   * forms), set bodyScroll. The panel becomes a flex column and the children
   * region scrolls. Default false (the whole panel scrolls) -- matches the
   * common short-modal layout.
   */
  bodyScroll?: boolean;
  /**
   * Show the discreet in-modal feedback trigger (#628) in the header, next to the close button.
   * Lets a user point-and-click / text-select an element inside this modal and open a pre-filled
   * feedback report. Default true; set false on utility/confirm dialogs that should suppress it.
   */
  feedbackTrigger?: boolean;
  /**
   * Stable ``data-tour="..."`` anchor on the dialog panel so a guided tour
   * (EPIC #1321 / issue #1342) can point a modal-walkthrough step at the open
   * modal. Optional -- modals not used in a tour omit it.
   */
  dataTour?: string;
  /**
   * Stacking class for the backdrop. Default "z-50". A modal opened from
   * inside a legacy hand-rolled overlay that sits higher (e.g. a z-[60] edit
   * dialog) passes a higher layer so it is never hidden behind its opener.
   */
  zIndexClass?: string;
  /**
   * Where the panel sits from `sm:` up. "center" (default) is the classic
   * centred dialog. "corner" docks it bottom-right over an undimmed page -- a
   * floating assistant panel (D10-UI-002). Mobile is the bottom sheet either way.
   */
  placement?: "center" | "corner";
  /** Extra header controls rendered before the close button. */
  headerActions?: ReactNode;
  /** Element to focus on open instead of the first focusable control. */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

/** Selector for elements that can receive focus inside the dialog. */
const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]), ' +
  'select:not([disabled]), textarea:not([disabled]), iframe, object, embed, ' +
  '[tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export default function Modal({
  open = true,
  onClose,
  title,
  titleIcon,
  children,
  footer,
  size = "lg",
  closeOnBackdrop = true,
  closeOnEscape = true,
  closeDisabled = false,
  hideClose = false,
  closeLabel = "Close",
  className = "",
  bodyScroll = false,
  feedbackTrigger = true,
  dataTour,
  zIndexClass = "z-50",
  placement = "center",
  headerActions,
  initialFocusRef,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // The element that was focused before the modal opened -- restored on close.
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();

  useBodyScrollLock(open);

  const focusableEls = useCallback((): HTMLElement[] => {
    const root = panelRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }, []);

  // On open: remember the trigger, then move focus into the dialog. On close
  // (unmount or open->false): restore focus to the trigger.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    // Defer so the panel and its children are in the DOM before we focus.
    const id = window.setTimeout(() => {
      const els = focusableEls();
      // Prefer the first focusable that is not the close button so the user
      // lands on a meaningful control; fall back to the panel itself.
      const target = initialFocusRef?.current ?? els[0] ?? panelRef.current;
      target?.focus();
    }, 0);

    return () => {
      window.clearTimeout(id);
      const prev = previouslyFocused.current;
      // Only restore if the element is still in the document and focusable.
      if (prev && document.contains(prev)) {
        prev.focus();
      }
    };
    // initialFocusRef is a ref object: read at open time, never a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusableEls]);

  // Escape to close + Tab focus-trap, scoped to the dialog while open.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (closeOnEscape && !closeDisabled) onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusableEls();
      if (els.length === 0) {
        // Nothing focusable -- keep focus on the panel.
        e.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !panelRef.current?.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !panelRef.current?.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, focusableEls, closeOnEscape, closeDisabled]);

  if (!open) return null;

  // Portal to document.body so the fixed overlay escapes any ancestor that
  // establishes a containing block (the frosted-glass header's backdrop-blur,
  // a transformed card, ...) and always fills the true viewport (#1815).
  return createPortal(
    <div
      className={
        `crm-overlay-backdrop-enter fixed inset-0 ${zIndexClass} flex items-end justify-center bg-black/40 p-0 ` +
        (placement === "corner"
          ? "sm:items-end sm:justify-end sm:bg-transparent sm:p-6"
          : "sm:items-center sm:p-4")
      }
      role="presentation"
      onClick={closeOnBackdrop && !closeDisabled ? onClose : undefined}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-tour={dataTour}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={
          "crm-overlay-enter w-full min-w-0 bg-[var(--color-bg-card)] rounded-t-xl sm:rounded-lg shadow-xl " +
          // Clamp horizontal overflow so a wide child can never push the panel
          // beyond the column and produce a horizontal scrollbar (#626). Vertical
          // scroll is unaffected: the whole panel (overflow-y-auto) or the body
          // region (flex-col + body overflow-y-auto) still scrolls.
          "max-h-[95vh] overflow-x-hidden focus:outline-hidden " +
          (bodyScroll ? "flex flex-col " : "overflow-y-auto ") +
          SIZE_CLASS[size] +
          (className ? ` ${className}` : "")
        }
      >
        {/* Header */}
        <div
          className={
            "flex items-center justify-between px-5 sm:px-6 py-4 " +
            "border-b border-[var(--color-border)]" +
            (bodyScroll ? " shrink-0" : "")
          }
        >
          <h2
            id={titleId}
            className="flex items-center gap-2 text-lg font-semibold text-[var(--color-text-primary)]"
          >
            {titleIcon}
            {title}
          </h2>
          <div className="flex items-center gap-0.5 -mr-2">
            {headerActions}
            {feedbackTrigger && (
              // Suspense fallback is empty: the trigger is non-essential chrome, so the header
              // simply renders without it until the small chunk resolves.
              <Suspense fallback={null}>
                <ModalAnnotateTrigger />
              </Suspense>
            )}
            {!hideClose && (
              <button
                type="button"
                onClick={onClose}
                disabled={closeDisabled}
                aria-label={closeLabel}
                className="p-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-page)] transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div
          className={
            "min-w-0 overflow-x-hidden" +
            (bodyScroll ? " flex-1 min-h-0 overflow-y-auto" : "")
          }
        >
          {children}
        </div>

        {/* Footer (optional, sticky in body-scroll mode) */}
        {footer && (
          <div
            className={
              "flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 sm:px-6 py-4 " +
              "border-t border-[var(--color-border)]" +
              (bodyScroll ? " shrink-0" : "")
            }
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  ,
    document.body,
  );
}
